import React from "react";
import { useDialogBehavior } from "../hooks/useDialogBehavior.js";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * MpxModal (Phase B-2 Task 2)
 *
 * Props:
 *  - open: boolean (default false) — 표시 여부
 *  - onClose: function — 닫기 핸들러
 *  - title: string | null — 헤더 타이틀
 *  - size: 'sm' | 'md' | 'lg' (default 'md')
 *  - closeOnBackdrop: boolean (default true)
 *  - closeOnEsc: boolean (default true)
 *  - footer: ReactNode | null
 *  - className: string
 *  - children: 본문
 *
 * 동작:
 *  - createPortal로 document.body 마운트
 *  - ESC 키 → onClose 호출 (closeOnEsc=true 시)
 *  - 배경 클릭 → onClose (closeOnBackdrop=true 시). 내부 패널 클릭은 stopPropagation
 *  - open=false → null 반환 (DOM 완전 제거)
 *  - 열림 시 body overflow=hidden, 닫힘 시 복원
 */
export default function MpxModal({
  open = false,
  onClose,
  title = null,
  size = "md",
  closeOnBackdrop = true,
  closeOnEsc = true,
  children,
  footer = null,
  className = "",
  overlayClassName = "",
}) {
  const panelRef = useDialogBehavior(open, onClose, closeOnEsc);

  if (!open) return null;

  const panelClasses = [
    "mpx-modal__panel",
    `mpx-modal__panel--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return createPortal(
    <div
      className={`mpx-modal__backdrop ${overlayClassName}`}
      // Rule 126: backdrop·body 양쪽 stopPropagation — closeOnBackdrop 여부와 무관하게
      // 전파는 항상 차단(핸들러 상시 부착), 닫기만 조건부.
      onClick={(e) => {
        e.stopPropagation();
        if (closeOnBackdrop) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={panelClasses}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title || "모달"}
      >
        {(title || onClose) && (
          <div className="mpx-modal__header">
            {title && <h2 className="mpx-modal__title" title={typeof title === "string" ? title : undefined}>{title}</h2>}
            {onClose && <button
              className="mpx-modal__close"
              onClick={onClose}
              aria-label="닫기"
              type="button"
            >
              <X size={16} strokeWidth={1.75} aria-hidden="true" />
            </button>}
          </div>
        )}

        <div className="mpx-modal__body">{children}</div>

        {footer && <div className="mpx-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
